import { useState } from 'react';
import { Link } from 'react-router-dom';
import { testimonials } from '@/mocks/testimonials';

const milestones = [
  { year: '2018', title: '扬帆起航', desc: '蜜桃传媒在北京正式成立，创始团队汇聚来自头部媒体和互联网平台的资深内容人，确立"让品牌内容更有温度"的核心使命。' },
  { year: '2019', title: '初露锋芒', desc: '完成天使轮融资，团队扩充至30人。与十余家知名品牌达成合作，在短视频和社交媒体营销领域积累首批标杆案例。' },
  { year: '2020', title: '逆势增长', desc: '疫情期间抓住线上内容需求爆发机遇，服务品牌客户突破100家，年营收增长200%。短视频运营和直播带货成为核心增长引擎。' },
  { year: '2021', title: '全面升级', desc: '完成A轮融资。团队规模突破80人，在北京、上海、杭州设立办公室。业务从内容制作延伸至品牌策略咨询。' },
  { year: '2022', title: '技术赋能', desc: '自主研发内容数据分析平台上线，实现内容创作全链路数字化。年度服务品牌客户突破300家，累计内容曝光量突破30亿。' },
  { year: '2023', title: '行业标杆', desc: '获评"年度最具影响力新媒体营销机构"，与小米、花西子、元气森林等头部品牌建立深度合作。新媒体运营服务覆盖全平台。' },
  { year: '2024', title: '生态布局', desc: '团队规模突破150人，业务扩展至IP孵化、达人经纪、品牌出海内容服务。平台累计服务品牌超500家，原创内容产出突破10000篇。' },
  { year: '2025', title: '持续进化', desc: 'AI+内容战略全面落地，创意效率提升300%。布局元宇宙内容营销和虚拟人IP业务，持续引领行业创新方向。' },
  { year: '2026', title: '全域增长', desc: '品牌客户续约率达92%，年度内容曝光量突破50亿。致力于成为全球领先的华语品牌内容创作平台，与合作伙伴共同成长。' },
];

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', company: '', phone: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMsg('提交成功！我们的商务团队将在24小时内与您联系。');
      setFormData({ name: '', company: '', phone: '', email: '', message: '' });
      return;
    }
    try {
      const formDataObj = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9somv14ne17ei0sdl80', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMsg('提交成功！我们的商务团队将在24小时内与您联系。');
        setFormData({ name: '', company: '', phone: '', email: '', message: '' });
      } else {
        const errMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMsg(errMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后重试');
    }
  };

  return (
    <main className="pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于蜜桃传媒',
            description: '蜜桃传媒是专注于优质内容创作与品牌传播的新媒体平台',
            url: 'https://www.mitaomedia.com/about',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.mitaomedia.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.mitaomedia.com/about' },
            ],
          }),
        }}
      />

      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600">关于我们</span>
          </nav>

          <div className="max-w-3xl mb-14">
            <h4 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              关于蜜桃传媒
            </h4>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-3">
              蜜桃传媒成立于2018年，是专注于优质内容创作与品牌传播的新媒体平台。我们从创立之初就坚信：<strong>好内容是最好的品牌代言人</strong>。
            </p>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-3">
              经过八年发展，蜜桃传媒已从最初的5人小团队成长为超过150人的专业内容营销集团，在北京、上海、杭州设有办公室，服务覆盖创意制作、短视频运营、社交媒体营销、品牌传播、新媒体运营等全链路内容服务。
            </p>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              我们已累计服务<strong>超过500家品牌客户</strong>，产出<strong>超过10000篇原创内容</strong>，累计内容曝光量<strong>突破50亿次</strong>。选择蜜桃传媒，让您的品牌内容更有温度、更具传播力。
            </p>
          </div>

          <div className="mb-14">
            <h4 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              发展历程
            </h4>
            <div className="relative">
              <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-background-200"></div>
              <div className="space-y-8 lg:space-y-0">
                {milestones.map((item, idx) => (
                  <div
                    key={item.year}
                    className={`relative flex flex-col lg:flex-row items-start gap-6 lg:gap-0 ${
                      idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                    }`}
                  >
                    <div className={`lg:w-1/2 ${idx % 2 === 0 ? 'lg:pr-16 lg:text-right' : 'lg:pl-16 lg:text-left'} ${idx % 2 === 0 ? '' : 'lg:ml-auto'}`}>
                      <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                        <span className="inline-block px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold mb-2">
                          {item.year}
                        </span>
                        <h5 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                          {item.title}
                        </h5>
                        <p className="text-foreground-500 text-sm leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                    <div className="hidden lg:flex absolute left-1/2 top-6 -translate-x-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 z-10"></div>
                    <div className="lg:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-14">
            <h4 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              客户口碑
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t, idx) => (
                <div key={idx} className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-700 font-medium text-sm">{t.name[0]}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.title} · {t.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div id="contact" className="max-w-2xl mx-auto">
            <h4 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3 text-center">
              联系我们
            </h4>
            <p className="text-foreground-500 text-sm text-center mb-8">
              如果您有内容营销需求，欢迎填写以下表单，我们的商务团队将在24小时内与您联系
            </p>
            <div className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
              <form onSubmit={handleSubmit} data-readdy-form="">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors placeholder:text-background-500"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      公司名称
                    </label>
                    <input
                      id="contact-company"
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors placeholder:text-background-500"
                      placeholder="请输入您的公司名称"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      联系电话 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors placeholder:text-background-500"
                      placeholder="请输入您的联系电话"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors placeholder:text-background-500"
                      placeholder="请输入您的电子邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    需求描述 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors placeholder:text-background-500 resize-none"
                    placeholder="请描述您的合作需求（限500字以内）"
                  />
                  <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
                </div>
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  提交合作需求
                </button>
                {formStatus !== 'idle' && (
                  <p className={`text-sm mt-3 text-center ${formStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
                    {formMsg}
                  </p>
                )}
              </form>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-phone-line text-lg text-primary-600"></i>
                </div>
                <div className="text-sm font-medium text-foreground-900 mb-1">商务热线</div>
                <div className="text-sm text-foreground-500">010-8888-6666</div>
              </div>
              <div className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-lg text-primary-600"></i>
                </div>
                <div className="text-sm font-medium text-foreground-900 mb-1">合作邮箱</div>
                <div className="text-sm text-foreground-500">bd@mitaomedia.com</div>
              </div>
              <div className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-lg text-primary-600"></i>
                </div>
                <div className="text-sm font-medium text-foreground-900 mb-1">公司地址</div>
                <div className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}